import React, { useEffect, useState } from 'react';
import _get from 'lodash/get';
import { client } from 'cccisd-apollo';
import IconUp from 'cccisd-icons/arrow-up2';
import { SecureMessagingMultipleConversations } from 'cccisd-laravel-secure-messaging';
import Loader from 'cccisd-loader';
import { useParams } from 'cccisd-react-router';
import messageParticipantsQuery from './messageParticipants.graphql';
import style from './style.css';

const Appdefs = window.cccisd.appDefs;
const Fortress = window.cccisd.fortress;

function TfhMessagingCoach() {
    const [conversations, setConversations] = useState(null);
    const [allParents, setAllParents] = useState(null);

    const params = useParams();
    let initialSelectedParticipantId = params.initialSelectedParticipantId;
    if (!Number.isNaN(parseInt(initialSelectedParticipantId, 10))) {
        initialSelectedParticipantId = parseInt(initialSelectedParticipantId, 10);
    }

    const coach = {
        id: Fortress.user.pawnId,
        username: Fortress.user.username,
        firstName: Fortress.user.firstName,
        lastName: Fortress.user.lastName,
    };

    useEffect(() => {
        async function getConversations() {
            const resp = await client.query({
                query: messageParticipantsQuery,
                variables: { pawnId: Fortress.user.pawnId },
                fetchPolicy: 'network-only',
            });

            const currClassList = _get(resp, 'data.groups.tfhClassList', []) || [];
            let currentParents = [];
            currClassList.forEach(tfhClass => {
                const parents = _get(tfhClass, 'descendantRoles.tfhParentList', []) || [];
                parents.forEach(p => {
                    currentParents.push({
                        id: p.pawn.pawnId,
                        ...p.user,
                    });
                });
            });

            const currParentPawnIdMap = currentParents.reduce((all, curr) => {
                all[curr.id] = true;
                return all;
            }, {});
            setAllParents(currentParents);

            // old convos are displayed in read-only mode; current coaches can be actively messaged
            const convos = _get(resp, 'data.messages.secureConversationList', []);
            const convosToDisplay = {};
            currentParents.forEach(parent => {
                convosToDisplay[parent.id] = {
                    readOnly: false,
                    ...parent,
                };
            });

            convos.forEach(convo => {
                const otherParticipant = convo.participantList.find(part => {
                    return part.pawn.pawnId !== Fortress.user.acting.id;
                });

                const isCurrentParent = otherParticipant.pawn.pawnId in currParentPawnIdMap;

                if (isCurrentParent || convo.messageCount === 0) {
                    return;
                }

                convosToDisplay[otherParticipant.pawn.pawnId] = {
                    readOnly: true,
                    id: otherParticipant.pawn.pawnId,
                    ...otherParticipant.user,
                };
            });

            setConversations(Object.values(convosToDisplay));
        }

        getConversations();
    }, []);

    function scrollToTop() {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth',
        });
    }

    if (conversations === null) {
        return <Loader loading />;
    }

    let noParentsWarning = null;
    let familyLabel = 'TFH Families';
    if (!Array.isArray(allParents) || allParents.length === 0) {
        try {
            familyLabel = Appdefs.pawn.roles.find(r => r.handle === 'tfhClass').labelPlural;
        } catch (e) {
            familyLabel = 'TFH Families';
        }
        noParentsWarning = (
            <div className="alert alert-info mb-4">
                <p className="mb-0">Messaging is not active because you have no {familyLabel} assigned.</p>
            </div>
        );
    }

    let coachLabelPlural = 'TFH Coach';
    try {
        coachLabelPlural = Appdefs.pawn.roles.find(r => r.handle === 'tfhInstructor').labelPlural;
    } catch (e) {
        coachLabelPlural = 'TFH Coach';
    }

    return (
        <div>
            {noParentsWarning}
            <div className="alert alert-light mb-4">
                <p className="mb-0">
                    These conversations can only be viewed by <b>{Fortress.user.acting.user.username}</b> and the
                    recipient. Other family members and {coachLabelPlural} are restricted from viewing these messages.
                </p>
                {conversations.some(convo => convo.readOnly) && (
                    <p className="mt-3 mb-0">Conversations with inactive {familyLabel} will be read-only.</p>
                )}
            </div>
            <SecureMessagingMultipleConversations
                primaryParticipant={coach}
                anotherParticipants={conversations}
                initialSelectedParticipantId={
                    conversations.some(convo => convo.id === initialSelectedParticipantId)
                        ? initialSelectedParticipantId
                        : undefined
                }
            />

            <div className={style.backToTop}>
                <button className="btn btn-link" type="button" onClick={scrollToTop}>
                    <IconUp spaceRight />
                    Scroll to Top
                </button>
            </div>
        </div>
    );
}

export default TfhMessagingCoach;
